import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TokenStorageService } from "./token-storage.service"

export enum UserAccessLevel {
  SCA_SUPER_ADMIN = 'SCA_SUPER_ADMIN',
  SCA_IES_ADMIN = 'SCA_IES_ADMIN',
  NONE = 'NONE'
}

export interface UserAccessContext {
  userId: string;
  username: string;
  accessLevel: UserAccessLevel;
  managedGroups: string[];
  userGroups: string[];
  roles: string[];
}

@Injectable({
  providedIn: 'root'
})
export class UserContextService {
  private contextSubject = new BehaviorSubject<UserAccessContext | null>(null);
  public context$ = this.contextSubject.asObservable();

  constructor(private tokenStorageService: TokenStorageService) {
    this.initializeContext();
  }

  /**
   * Initialise le contexte à partir du token stocké
   */
  private initializeContext(): void {
    const token = localStorage.getItem('access_token');
    if (token) {
      const context = this.extractContextFromToken(token);
    }
  }

  /**
   * Extrait le contexte utilisateur depuis le JWT
   */
  private extractContextFromToken(token: string): UserAccessContext | null {
    try {
      const payload = this.decodeJWT(token);

      if (!payload) {
        return null;
      }

      // const realmAccess = payload.realm_access || {};
      // const roles: string[] = realmAccess.roles || [];
      const roles: string[] = this.tokenStorageService.getRolesFromToken(token);

      // Extraire les groupes gérés
      let managedGroups: string[] = [];
      if (payload.groups) {
        if (typeof payload.groups === 'string') {
          managedGroups = payload.groups.split(',').map((g: string) => g.trim());
        } else if (Array.isArray(payload.groups)) {
          managedGroups = payload.groups;
        }
      }

      const context: UserAccessContext = {
        userId: payload.sub,
        username: payload.preferred_username,
        accessLevel: this.determineAccessLevel(roles),
        managedGroups: managedGroups,
        userGroups: payload.groups || [],
        roles: roles
      };

      return context;
    } catch (error) {
      console.error('Erreur lors de l\'extraction du contexte:', error);
      return null;
    }
  }

  /**
   * Décode un JWT
   */
  private decodeJWT(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Erreur décodage JWT:', error);
      return null;
    }
  }

  /**
   * Détermine le niveau d'accès selon les rôles
   */
  private determineAccessLevel(roles: string[]): UserAccessLevel {
    if (roles.includes('SCA_SUPER_ADMIN')) {
      return UserAccessLevel.SCA_SUPER_ADMIN;
    } else if (roles.includes('SCA_IES_ADMIN')) {
      return UserAccessLevel.SCA_IES_ADMIN;
    }
    return UserAccessLevel.NONE;
  }

  /**
   * Met à jour le contexte (appelé après login)
   */
  public updateContext(token: string): void {
    const context = this.extractContextFromToken(token);
    this.contextSubject.next(context);
  }

  /**
   * Obtient le contexte actuel
   */
  public getCurrentContext(): any | null {
    return this.contextSubject.value;
  }

  /**
   * Vérifie si l'utilisateur est super admin
   */
  public isSuperAdmin(): boolean {
    const context = this.getCurrentContext();
    return context?.accessLevel === UserAccessLevel.SCA_SUPER_ADMIN;
  }

  /**
   * Vérifie si l'utilisateur est admin de groupe
   */
  public isGroupAdmin(): boolean {
    const context = this.getCurrentContext();
    return context?.accessLevel === UserAccessLevel.SCA_IES_ADMIN;
  }

  /**
   * Obtient les groupes gérés
   */
  public getManagedGroups(): string[] {
    const context = this.getCurrentContext();
    return context?.managedGroups || [];
  }

  // public getManagedGroups(): Observable<any> {
  //   const context = this.getCurrentContext();
  //   return new Observable<string[]>(subscriber => {
  //     subscriber.next(context?.managedGroups || []);
  //     subscriber.complete();
  //   });
  // }

  /**
   * Nettoie le contexte (lors de la déconnexion)
   */
  public clearContext(): void {
    this.contextSubject.next(null);
  }

  public canManageGroup(groupPath: any): boolean {
    return true
  }
}